import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

import { t } from '../i18n/lingua'

/**
 * The portal: the console's areas — Discovery, Planning, Monitoring,
 * Security, Asset, Contracts, Compliance, Settings. The selector at the top
 * left opens them; the menu on the left shows only the sections of the area
 * in use. An area appears when the role sees at least one of its pages (the
 * custom roles stay the only rule); Monitoring only with Eagle. A page may
 * live in more than one area (Patching, Report, Audit…); a link opens it in
 * the area in use when it is there, else in its first one. See Layout.
 */

export type AreaId = 'discovery' | 'planning' | 'monitoring' | 'security' | 'devsecops' | 'asset' | 'contracts' | 'compliance' | 'censo_med' | 'censo_access' | 'settings'

export interface Area {
  id: AreaId
  label: string
  description: string
}

/** An area the user can open: its first page and how many pages it has. */
export interface AreaVisibile extends Area {
  prima: string
  pagine: number
}

export function costruisciAree(): Area[] {
  return [
    { id: 'discovery', label: t('Discovery'), description: t('Cosa c’è e come è collegato: raccolta, inventario, servizi, software, traffico, integrazioni.') },
    { id: 'planning', label: t('Planning'), description: t('Quanto margine resta, quanto costa, che cosa scade: capacity, costi cloud, cicli di vita, patching, report.') },
    { id: 'monitoring', label: t('Monitoring'), description: t('Come stanno adesso i sistemi che contano: cruscotto, allarmi, notifiche.') },
    { id: 'security', label: t('Security'), description: t('Postura e difese: vulnerabilità, cifratura, certificati, patching, sorgenti di sicurezza.') },
    { id: 'devsecops', label: t('DevSecOps'), description: t('Il software che l’organizzazione produce: pipeline e SBOM dei build, registri, vulnerabilità, cancello di sicurezza, Renovate, VEX.') },
    { id: 'asset', label: t('Asset'), description: t('I beni dell’organizzazione, le licenze del software e le anagrafiche.') },
    { id: 'contracts', label: t('Suppliers & Contracts'), description: t('Il registro dei fornitori con quello che gira di loro, i contratti, le scadenze e i rinnovi, le licenze del software.') },
    { id: 'compliance', label: t('Compliance'), description: t('NIS2, DORA e CRA, con i registri e i report che servono a dimostrarlo.') },
    // CENSO MED (ADR-0027): only with the licence (`useMed`), like Monitoring with Eagle.
    { id: 'censo_med', label: t('CENSO MED'), description: t('Gli elettromedicali per l’Ingegneria clinica: dove sono, schede, cespiti e contratti, verifiche.') },
    // CENSO ACCESS (ADR-0030): only with the licence and a role naming it (`useStatoAccess`).
    { id: 'censo_access', label: t('CENSO ACCESS'), description: t('Il registro degli accessi di amministratori e utenti: inalterabile, legato all’inventario, consultabile solo da chi ne ha il ruolo.') },
    { id: 'settings', label: t('Settings'), description: t('Utenti e ruoli, impostazioni, chiavi API, riconoscimento, audit.') },
  ]
}

export function IconaArea({ id, size = 20 }: { id: AreaId | 'griglia' | 'home'; size?: number }) {
  const tratti: Record<string, ReactNode> = {
    griglia: (
      <>
        {[5, 12, 19].flatMap((x) => [5, 12, 19].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" />))}
      </>
    ),
    home: <path d="M4 11.5 12 4.5l8 7M6.5 9.5V19.5h4v-5h3v5h4V9.5" />,
    // Discovery: a radar.
    discovery: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <path d="M12 12 17.5 6.5" />
      </>
    ),
    // Planning: a calendar with a rising line.
    planning: (
      <>
        <rect x="3.5" y="5" width="17" height="15" rx="2" />
        <path d="M3.5 9.5h17M8 3.5v3m8-3v3M7 17l3-3 2.5 2 4-4.5" />
      </>
    ),
    // Monitoring: a pulse.
    monitoring: <path d="M3 12h4l2-5 4 10 2-5h6" />,
    security: <path d="M12 3.5 18.5 6v5.2c0 4.6-3.2 7.5-6.5 9.3-3.3-1.8-6.5-4.7-6.5-9.3V6L12 3.5Z" />,
    // DevSecOps: the loop of build and release, with a check.
    devsecops: (
      <>
        <path d="M12 12c-2-2.6-3.6-4-5.5-4a4 4 0 0 0 0 8c1.9 0 3.5-1.4 5.5-4Zm0 0c2 2.6 3.6 4 5.5 4a4 4 0 0 0 0-8c-1.9 0-3.5 1.4-5.5 4Z" />
      </>
    ),
    // Asset: a box.
    asset: (
      <>
        <path d="M12 3.5 20 7.5v9l-8 4-8-4v-9l8-4Z" />
        <path d="m4 7.5 8 4 8-4M12 11.5v9" />
      </>
    ),
    // Contracts: a document with a signature.
    contracts: (
      <>
        <path d="M6.5 3.5h8l4 4v13h-12v-17Z" />
        <path d="M14.5 3.5v4h4M9 12h6m-6 3h4m-4 3.5c1-1.3 2-1.3 3 0s2 1.3 3 0" />
      </>
    ),
    // Compliance: a badge with a check.
    compliance: (
      <>
        <circle cx="12" cy="10" r="6" />
        <path d="m9.5 10 1.8 1.8 3.2-3.4M8.5 15l-1.5 5.5 5-2.5 5 2.5-1.5-5.5" />
      </>
    ),
    // CENSO MED: a heart with a pulse.
    censo_med: (
      <>
        <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.3 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />
        <path d="M6 12.5h3l1.5-2.5 2 4 1.5-1.5H18" />
      </>
    ),
    // CENSO ACCESS: a key in a shield.
    censo_access: (
      <>
        <path d="M12 3.5 18.5 6v5.2c0 4.6-3.2 7.5-6.5 9.3-3.3-1.8-6.5-4.7-6.5-9.3V6L12 3.5Z" />
        <circle cx="12" cy="10" r="2" />
        <path d="M12 12v4m0-2h1.6" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3.5v2.2m0 12.6v2.2M3.5 12h2.2m12.6 0h2.2M6 6l1.6 1.6m8.8 8.8L18 18M6 18l1.6-1.6m8.8-8.8L18 6" />
      </>
    ),
  }
  return (
    <svg className="portale-icona" viewBox="0 0 24 24" width={size} height={size} fill={id === 'griglia' ? 'currentColor' : 'none'}
      stroke={id === 'griglia' ? 'none' : 'currentColor'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {tratti[id]}
    </svg>
  )
}

/** What Layout gives the pages: the areas the user can open, and how to go there. */
export interface Portale {
  aree: AreaVisibile[]
  corrente: AreaId | null
  vai: (id: AreaId) => void
}

export const PortaleContext = createContext<Portale>({ aree: [], corrente: null, vai: () => undefined })

export function usePortale(): Portale {
  return useContext(PortaleContext)
}

/** The tiles of the areas: in the selector and on the home page. */
export function GrigliaAree({ aree, corrente, onScegli, compatta = false }: {
  aree: AreaVisibile[]
  corrente: AreaId | null
  onScegli: (id: AreaId) => void
  compatta?: boolean
}) {
  return (
    <div className={`portale-griglia${compatta ? ' portale-griglia--compatta' : ''}`} role="list">
      {aree.map((a) => (
        <button key={a.id} type="button" role="listitem" data-area={a.id}
          className={`portale-tessera${a.id === corrente ? ' portale-tessera--corrente' : ''}`}
          aria-current={a.id === corrente ? 'true' : undefined} onClick={() => onScegli(a.id)}>
          <span className="portale-tessera__icona"><IconaArea id={a.id} size={compatta ? 22 : 26} /></span>
          <span className="portale-tessera__testo">
            <strong>{a.label}</strong>
            <small>{a.description}</small>
          </span>
        </button>
      ))}
    </div>
  )
}

/** The selector at the top left: the area in use, and the panel of all of them. */
export function SelettoreAree({ aree, corrente, onScegli }: {
  aree: AreaVisibile[]
  corrente: AreaId | null
  onScegli: (id: AreaId) => void
}) {
  const [aperto, setAperto] = useState(false)
  const radice = useRef<HTMLDivElement | null>(null)
  const area = aree.find((a) => a.id === corrente)

  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => {
      if (!radice.current?.contains(e.target as Node)) setAperto(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAperto(false)
    }
    document.addEventListener('mousedown', fuori)
    window.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', fuori)
      window.removeEventListener('keydown', esc)
    }
  }, [aperto])

  if (aree.length === 0) return null
  return (
    <div className="portale-selettore" ref={radice}>
      <button type="button" className="portale-selettore__pulsante" aria-haspopup="true" aria-expanded={aperto}
        title={t('Aree della console')} onClick={() => setAperto((x) => !x)}>
        <IconaArea id="griglia" size={18} />
        <span className="portale-selettore__nome">{area?.label ?? t('Aree')}</span>
      </button>
      {aperto && (
        <div className="portale-pannello" role="dialog" aria-label={t('Aree della console')}>
          <GrigliaAree aree={aree} corrente={corrente} compatta onScegli={(id) => { setAperto(false); onScegli(id) }} />
        </div>
      )}
    </div>
  )
}
